import * as React from "react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { AXIOS_API_INSTANCE } from "../../../Constants"
import { send_authorized_request } from "../../../Constants/cognito"
import { useSnackbar } from 'notistack';
import { Box, IconButton } from "@mui/material"
import Grid from "@mui/material/Grid2"
import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ImageIcon from '@mui/icons-material/Image';
import { CircularProgress } from '@mui/material';
import dayjs from "dayjs"
import UserAvatar from "../../UserAvatar"
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid"
import FileImageModal from "./FileImageModal"
import ConfirmModal from "../../modals/ConfirmModal"
import { LoadingBackdropIndeterminate } from "../../LoadingBackdrop"
import { useTrackedUser } from "../../UserState"
import { findUser, useUsers } from "../../ReactQuery/QueryUsers"

const check_if_image = (file_name) => {
    const split_name = file_name.split('.')
    if (['apng', 'avif', 'gif', 'png', 'svg', 'webp', 'jpg', 'jpeg', 'jfif', 'pjpeg', 'pjp'].includes(split_name[split_name.length - 1])) {
        return true
    } else {
        return false
    }
}

export default function ServiceOrderFilesModal({
    open,
    serviceOrder,
    uploadNewFile,
    closeModal
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [my_user,] = useTrackedUser();
    const { data: users, } = useUsers();
    const [serviceOrderFiles, setServiceOrderFiles] = React.useState(undefined)
    const [serviceOrderFilesLoadingStep, setServiceOrderFilesLoadingStep] = React.useState(0)
    const [serviceOrderFilesUploadStep, setServiceOrderFilesUploadStep] = React.useState(0)
    const [serviceOrderFilesDeletingStep, setServiceOrderFilesDeletingStep] = React.useState(0)
    const [selectedImage, setSelectedImage] = React.useState(undefined)
    const [confirmation, setConfirmation] = React.useState({
        open: false,
        text: '',
        callback: null
    })

    const columns = [
        {
            field: 'file_name',
            headerName: 'File Name',
            flex: 1,
            editable: false,
            sortable: false,
            filterable: false,
        },
        {
            field: 'date_updated',
            headerName: 'Date Uploaded',
            type: 'date',
            flex: 1,
            editable: false,
            sortable: false,
            filterable: false,
            valueGetter: (value, row) => dayjs.utc(row.date_updated).toDate()
        },
        {
            field: 'user',
            headerName: 'Uploaded By',
            flex: 1,
            editable: false,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const user = findUser(users, params.row.user_id)
                return (
                    (<Grid container space={1} alignItems="center">
                        <Grid sx={{ pr: 1 }} size="auto">
                            <span><UserAvatar user={user} /></span>
                        </Grid>
                        <Grid size="auto">
                            <span>{user?.name}</span>
                        </Grid>
                    </Grid>)
                );
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: (params) => {
                return [
                    <GridActionsCellItem
                        icon={check_if_image(params.row.file_name) ? <ImageIcon /> : <FileDownloadIcon />}
                        label="Download"
                        className="textPrimary"
                        onClick={() => handleDownloadClick(params.row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon color="error"/>}
                        label="Delete"
                        onClick={() => handleDeleteClick(params.row)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    const getFiles = () => {
        setServiceOrderFilesLoadingStep(1)
        send_authorized_request(() => AXIOS_API_INSTANCE.get(`serviceorders/${serviceOrder.id}/files`).then((serviceOrderFiles) => {
            setServiceOrderFiles((serviceOrderFiles.data.data ?? []).sort((a, b) => dayjs.utc(b.date_updated).unix() - dayjs.utc(a.date_updated).unix()))
            setServiceOrderFilesLoadingStep(0)
        }).catch((error) => {
            console.log(error);
            enqueueSnackbar(`Failed to load service order files: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
            setServiceOrderFilesLoadingStep(0)
        }))
    }

    const handleDownloadClick = (file) => {
        if (check_if_image(file.file_name)) {
            setSelectedImage(file)
        } else {
            const url = `/customerfiles/client/${my_user.client.id}/serviceorders/${serviceOrder.id}/${file.file_name}`
            window.open(url)
        }
    }
    const handleDeleteClick = (file) => {
        setConfirmation({
            open: true,
            text: `Are you sure you want to delete file ${file.file_name}?`,
            callback: (confirmation) => {
                if (confirmation) {
                    setServiceOrderFilesDeletingStep(1)
                    send_authorized_request(() =>
                        AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrder.id}/remove_file`, { id: file.id },).then((response) => {
                            enqueueSnackbar('Successfully deleted file', { variant: 'success' })
                            setServiceOrderFilesDeletingStep(0)
                            getFiles()
                        }).catch((error) => {
                            console.log(error);
                            enqueueSnackbar(`Failed to delete file: ${error?.response ?? "Unknown Error Occurred"}`, { variant: 'error' })
                            setServiceOrderFilesDeletingStep(0)
                        })
                    )
                }
                setConfirmation({ text: '', open: false })
            }
        })
    }

    React.useEffect(() => {
        if (serviceOrder) {
            setServiceOrderFiles(undefined)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceOrder])

    React.useEffect(() => {
        if (serviceOrderFiles === undefined && open) {
            getFiles()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, serviceOrderFiles])

    return (
        <div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                maxWidth="md"
                fullWidth
            >
                <LoadingBackdropIndeterminate
                    open={serviceOrderFilesUploadStep > 0}
                    text={"Uploading File"}
                />
                <LoadingBackdropIndeterminate
                    open={serviceOrderFilesDeletingStep > 0}
                    text={"Deleting File"}
                />
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>Service Order Files</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {serviceOrderFilesLoadingStep > 0 &&
                        <CircularProgress style={{ display: 'block', margin: '20% auto' }} color="inherit" />
                    }
                    {!serviceOrderFilesLoadingStep > 0 &&
                        <Grid container direction={'column'} spacing={1}>
                            <Grid>
                                <Button variant="contained" sx={{ mb: 1 }}>
                                    <input id="upload-service-order-file" type="file" accept="*" hidden onChange={(e) => uploadNewFile(e, setServiceOrderFilesUploadStep)} />
                                    <label htmlFor="upload-service-order-file" style={{ cursor: 'inherit' }}>
                                        <Box sx={{ display: 'flex' }}>
                                            <FileUploadIcon /><Box sx={{ pl: 1 }}>Add File</Box>
                                        </Box>
                                    </label>
                                </Button>
                            </Grid>
                            <Grid>
                                <DataGrid
                                    rows={(serviceOrderFiles ?? [])}
                                    columns={columns}
                                    disableRowSelectionOnClick
                                    autoHeight={true}
                                    hideFooter
                                />
                            </Grid>
                        </Grid>
                    }
                </DialogContent>
                <FileImageModal
                    header={selectedImage?.file_name}
                    url={`/customerfiles/client/${my_user.client.id}/serviceorders/${serviceOrder.id}/${selectedImage?.file_name}`}
                    open={selectedImage !== undefined}
                    closeModal={() => setSelectedImage(undefined)}
                />
                <ConfirmModal
                    {...confirmation}
                />
            </Dialog>
        </div>
    )
}
